/**
 * Public storefront — anonymous customers browse the inventory.
 * Route: /catalogo?q=&category=&brand=&v=VENDOR_ID
 *
 * The `v` param attributes future purchases/registrations to a specific
 * vendor; it is stored silently in localStorage as `alendra_referrer` and
 * never displayed to the visitor. Each vendor copies their personalised
 * link from the /products page.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { useSpecSchemas } from "@/lib/specSchemas";
import { MagnifyingGlass, ShoppingCart, X, Plus } from "@phosphor-icons/react";
import { useCart } from "@/context/CartContext";
import { CartFloatingButton, CartDrawer } from "@/components/storefront/CartDrawer";
import { trackClick, useImpressionTracker } from "@/lib/exposure";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtPrice = (n, cur) =>
  `${cur === "USD" ? "US$" : "$"}${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export default function Storefront() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const brand = params.get("brand") || "";
  const useCase = params.get("use_case") || "";
  const priceMin = params.get("min") || "";
  const priceMax = params.get("max") || "";
  const onlyOffers = params.get("offers") === "1";
  const sort = params.get("sort") || "";
  const onlyFree = params.get("free") === "1";
  const v = params.get("v") || "";
  const specParams = Array.from(params.entries()).filter(([k, val]) => k.startsWith("s_") && val);
  const specKey = specParams.map(([k, val]) => `${k}=${val}`).join("&");

  const [data, setData] = useState({ products: [], categories: [], brands: [], company: {} });
  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(true);

  // Persist the vendor referral for later (checkout / customer registration).
  useEffect(() => {
    if (v) {
      try { localStorage.setItem("alendra_referrer", v); } catch { /* ignore */ }
    }
  }, [v]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (category) qs.set("category", category);
    if (brand) qs.set("brand", brand);
    if (useCase) qs.set("use_case", useCase);
    if (priceMin) qs.set("min", priceMin);
    if (priceMax) qs.set("max", priceMax);
    if (onlyOffers) qs.set("offers", "1");
    if (v) qs.set("v", v);
    specParams.forEach(([k, val]) => qs.set(k, val));
    axios
      .get(`${API}/api/public/storefront?${qs.toString()}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ products: [], categories: [], brands: [], company: {} }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    // Fetch combos in parallel (don't block the main listing)
    axios.get(`${API}/api/public/combos`).then((r) => { if (!cancelled) setCombos(r.data || []); }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, category, brand, useCase, priceMin, priceMax, onlyOffers, v, specKey]);

  const updateParam = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    // Los filtros de ficha técnica dependen de la categoría: al cambiarla se limpian.
    if (key === "category") Array.from(next.keys()).filter((k) => k.startsWith("s_")).forEach((k) => next.delete(k));
    setParams(next, { replace: true });
  };

  const company = data.company || {};
  const baseProducts = data.products || [];

  // Effective price = promo.promo_price if there's an active promotion,
  // otherwise the regular store_price. Used by the "Ordenar" filter.
  //
  // Combos are merged into the product grid as first-class citizens (tagged
  // with `kind: "combo"`) so they intersperse with promos at the top of the
  // grid instead of living in a separate section.
  const products = useMemo(() => {
    const comboItems = combos
      .filter((c) => (onlyFree ? c.free_shipping !== false : true))
      .map((c) => ({
        __kind: "combo",
        id: c.id,
        name: c.name,
        photos: c.photos || [],
        store_price: Number(c.price || 0),
        sale_currency: c.currency || "MXN",
        // Give combos a synthetic "promotion" marker so the sort keeps them in
        // the top bucket alongside real promoted products.
        promotion: { promo_price: Number(c.price || 0) },
        __combo: c,  // keep the raw combo for the card
      }));
    let arr = onlyFree
      ? baseProducts.filter((p) => (p?.promotion ? !!p.promotion.free_shipping : !!p.free_shipping))
      : baseProducts;
    // Prepend combos so they render before promoted products by default.
    arr = [...comboItems, ...arr];
    const promoRank = (p) => (p?.__kind === "combo" || p?.promotion ? 0 : 1);
    const effective = (p) => Number(p?.promotion?.promo_price ?? p?.store_price ?? 0);
    arr = [...arr];
    if (sort === "price_asc") {
      arr.sort((a, b) => promoRank(a) - promoRank(b) || effective(a) - effective(b));
    } else if (sort === "price_desc") {
      arr.sort((a, b) => promoRank(a) - promoRank(b) || effective(b) - effective(a));
    } else if (sort === "name_asc") {
      arr.sort((a, b) => promoRank(a) - promoRank(b) || (a.name || "").localeCompare(b.name || "", "es"));
    }
    return arr;
  }, [baseProducts, combos, sort, onlyFree]);

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white border-b border-zinc-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Link to="/catalogo" className="flex items-center gap-2" data-testid="store-home-link">
            {company.logo_url ? (
              <img src={company.logo_url} alt={company.name} className="h-9 w-9 object-contain rounded" />
            ) : (
              <div className="h-9 w-9 rounded bg-[#002FA7] text-white font-heading font-bold flex items-center justify-center">A</div>
            )}
            <div>
              <div className="font-heading text-lg font-extrabold leading-tight">{company.name || "Alendra PC"}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Catálogo</div>
            </div>
          </Link>
          <div className="hidden sm:block text-xs text-zinc-500">
            {products.length} {products.length === 1 ? "producto" : "productos"} disponibles
          </div>
          <CartFloatingButton />
        </div>
        {/* Filter bar */}
        <div className="border-t border-zinc-100 bg-zinc-50">
          <div className="max-w-7xl mx-auto px-4 py-2.5 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                value={q}
                onChange={(e) => updateParam("q", e.target.value)}
                placeholder="Buscar..."
                className="w-full pl-9 pr-3 h-9 text-sm border border-zinc-300 rounded-md bg-white focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none"
                data-testid="store-search-input"
              />
            </div>
            <select
              value={category}
              onChange={(e) => updateParam("category", e.target.value)}
              className="h-9 px-2 text-sm border border-zinc-300 rounded-md bg-white"
              data-testid="store-category-filter"
            >
              <option value="">Todas las categorías</option>
              {(data.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={brand}
              onChange={(e) => updateParam("brand", e.target.value)}
              className="h-9 px-2 text-sm border border-zinc-300 rounded-md bg-white"
              data-testid="store-brand-filter"
            >
              <option value="">Todas las marcas</option>
              {(data.brands || []).map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <select
              value={sort}
              onChange={(e) => updateParam("sort", e.target.value)}
              className="h-9 px-2 text-sm border border-zinc-300 rounded-md bg-white"
              data-testid="store-sort-filter"
              aria-label="Ordenar productos"
            >
              <option value="">Ordenar: Relevancia</option>
              <option value="price_asc">Menor precio</option>
              <option value="price_desc">Mayor precio</option>
              <option value="name_asc">Nombre A–Z</option>
            </select>
            <button
              type="button"
              onClick={() => updateParam("free", onlyFree ? "" : "1")}
              className={`h-9 px-3 text-xs font-semibold rounded-md inline-flex items-center gap-1.5 border transition-colors ${
                onlyFree
                  ? "bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700"
                  : "bg-white border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              }`}
              data-testid="store-free-shipping-toggle"
              aria-pressed={onlyFree}
              title="Mostrar sólo productos con envío gratis"
            >
              🚚 Envío gratis
            </button>
            {(q || category || brand || useCase || priceMin || priceMax || onlyOffers || sort || onlyFree || specParams.length > 0) && (
              <button
                onClick={() => setParams(v ? `?v=${encodeURIComponent(v)}` : "", { replace: true })}
                className="text-xs text-[#002FA7] hover:underline inline-flex items-center gap-1"
                data-testid="store-clear-filters"
              >
                <X size={12} weight="bold" /> Limpiar filtros
              </button>
            )}
          </div>
        </div>
        {/* Filtros de ficha técnica (dependen de la categoría elegida) */}
        {category && (data.spec_facets || []).length > 0 && (
          <div className="border-t border-zinc-100 bg-white">
            <div className="max-w-7xl mx-auto px-4 py-2 flex flex-wrap items-center gap-2" data-testid="store-spec-facets">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mr-1">Ficha técnica</span>
              {data.spec_facets.map((f) => {
                const cur = params.get(`s_${f.key}`) || "";
                return (
                  <select
                    key={f.key}
                    value={cur}
                    onChange={(e) => updateParam(`s_${f.key}`, e.target.value)}
                    className={`h-8 px-2 text-xs border rounded-md ${cur ? "border-[#002FA7] text-[#002FA7] bg-[#002FA7]/5 font-semibold" : "border-zinc-300 bg-white"}`}
                    data-testid={`store-spec-filter-${f.key}`}
                    aria-label={f.label}
                  >
                    <option value="">{f.label}: todas</option>
                    {f.values.map((vv) => <option key={vv.value} value={vv.value}>{vv.value} ({vv.count})</option>)}
                  </select>
                );
              })}
            </div>
          </div>
        )}
      </header>

      {/* Chips de filtros extra provenientes del home (use_case, presupuesto, ofertas) */}
      {(useCase || priceMin || priceMax || onlyOffers || specParams.length > 0) && (
        <div className="max-w-7xl mx-auto px-4 pt-4 flex items-center gap-2 flex-wrap" data-testid="store-active-chips">
          {specParams.map(([k, val]) => (
            <FilterChip key={k} label={`${(data.spec_schema || []).find((f) => f.key === k.slice(2))?.label || k.slice(2)}: ${val}`} onClear={() => updateParam(k, "")} testid={`chip-spec-${k.slice(2)}`} />
          ))}
          {useCase && (
            <FilterChip label={`Uso: ${useCase}`} onClear={() => updateParam("use_case", "")} testid="chip-use-case" />
          )}
          {(priceMin || priceMax) && (
            <FilterChip
              label={`Precio: ${priceMin ? `$${Number(priceMin).toLocaleString("es-MX")}` : "$0"}${priceMax ? ` - $${Number(priceMax).toLocaleString("es-MX")}` : "+"}`}
              onClear={() => { updateParam("min", ""); updateParam("max", ""); }}
              testid="chip-price-range"
            />
          )}
          {onlyOffers && (
            <FilterChip label="Solo ofertas" onClear={() => updateParam("offers", "")} testid="chip-offers" />
          )}
        </div>
      )}

      {/* Grid */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-white border border-zinc-200 rounded-md overflow-hidden animate-pulse">
                <div className="aspect-square bg-zinc-100" />
                <div className="p-3 space-y-2">
                  <div className="h-3 bg-zinc-100 rounded w-3/4" />
                  <div className="h-3 bg-zinc-100 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <ShoppingCart size={48} className="mx-auto text-zinc-300 mb-3" />
            <h2 className="font-heading text-xl font-bold text-zinc-700">Sin productos por ahora</h2>
            <p className="text-sm text-zinc-500 mt-1">Prueba con otra búsqueda o limpia los filtros.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="store-product-grid">
            {products.map((p) => (
              p.__kind === "combo"
                ? <ComboCard key={`combo-${p.id}`} combo={p.__combo} />
                : <ProductCard key={p.id} product={p} vendorRef={v} />
            ))}
          </div>
        )}
      </main>

      <footer className="border-t border-zinc-200 bg-white mt-12">
        <div className="max-w-7xl mx-auto px-4 py-6 text-center text-xs text-zinc-500">
          © {new Date().getFullYear()} {company.name || "Alendra PC"} · Todos los productos sujetos a disponibilidad.
        </div>
      </footer>

      <CartDrawer />
    </div>
  );
}

/** Chip visible arriba del grid que muestra un filtro activo con opción de quitar. */
function FilterChip({ label, onClear, testid }) {
  return (
    <span className="inline-flex items-center gap-1 bg-[#EEF3FF] text-[#002FA7] text-xs font-semibold px-3 py-1 rounded-full" data-testid={testid}>
      {label}
      <button type="button" onClick={onClear} className="hover:text-[#001a5c]" aria-label="Quitar filtro">
        <X size={11} weight="bold" />
      </button>
    </span>
  );
}


function ProductCard({ product, vendorRef }) {
  const { addItem } = useCart();
  const promo = product.promotion;
  // Use promo photos if available; otherwise fall back to product photos
  const promoPhotos = Array.isArray(promo?.photos) ? promo.photos.filter(Boolean) : [];
  const basePhotos = Array.isArray(product.photos) ? product.photos.filter(Boolean) : [];
  const photos = promoPhotos.length ? promoPhotos : basePhotos;
  const photo = photos[0];
  const currency = product.sale_currency || "MXN";
  const href = `/catalogo/p/${product.id}${vendorRef ? `?v=${encodeURIComponent(vendorRef)}` : ""}`;
  const canAdd = Number(product.max_qty || 0) > 0;
  // Phase 4 — exposure tracking
  const trackKind = promo ? "promotion" : "product";
  const trackRefId = promo ? promo.id : product.id;
  const impressionRef = useImpressionTracker({ kind: trackKind, ref_id: trackRefId, section: "storefront_grid" });
  // Override product price + currency when a promotion is active so the cart
  // gets the right number when "Agregar" is pressed.
  const productForCart = promo ? {
    ...product,
    store_price: promo.promo_price,
    sale_currency: promo.currency || currency,
  } : product;
  const discountPct = promo && product.store_price > 0
    ? Math.round((1 - promo.promo_price / product.store_price) * 100) : 0;
  const freeShipping = promo ? !!promo.free_shipping : !!product.free_shipping;
  return (
    <div
      ref={impressionRef}
      className="group relative text-left bg-white border border-zinc-200 rounded-md overflow-hidden hover:shadow-md hover:border-[#002FA7]/30 transition-all flex flex-col"
      data-testid={`store-card-${product.id}`}
    >
      {freeShipping && (
        <span
          className="absolute top-2 right-2 z-10 bg-emerald-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-sm tracking-wide shadow inline-flex items-center gap-1"
          data-testid={`free-shipping-${product.id}`}
          title="Envío gratis en Tijuana, Rosarito y Tecate"
        >
          🚚 Envío gratis
        </span>
      )}
      {promo && (
        <div className="absolute top-2 left-2 z-10 flex flex-col gap-1" data-testid={`promo-badge-${product.id}`}>
          <span className="bg-[#E0000F] text-white text-[10px] font-extrabold px-2 py-0.5 rounded-sm tracking-wider shadow">
            {promo.badge_label || "PROMO"}
          </span>
          {discountPct > 0 && (
            <span className="bg-white border border-[#E0000F] text-[#E0000F] text-[10px] font-bold px-2 py-0.5 rounded-sm shadow-sm">
              -{discountPct}%
            </span>
          )}
        </div>
      )}
      <Link to={href} className="block">
        <div className="aspect-square bg-zinc-50 overflow-hidden">
          {photo ? (
            <img
              src={photo}
              alt={product.name}
              loading="lazy"
              className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-zinc-300 text-xs font-mono uppercase tracking-wider">
              Sin foto
            </div>
          )}
        </div>
        <div className="p-3 pb-2">
          {product.brand && <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{product.brand}</div>}
          <div className="font-medium text-sm leading-tight line-clamp-2 min-h-[2.5rem]">{product.name}</div>
          <SpecChips product={product} />
          <ConditionBadges product={product} className="mt-1.5" />
          <div className="mt-2 flex items-baseline justify-between">
            {promo ? (
              <div className="flex items-baseline gap-2">
                <span className="font-heading text-lg font-extrabold text-[#E0000F]" data-testid={`promo-price-${product.id}`}>
                  {fmtPrice(promo.promo_price, promo.currency || currency)}
                </span>
                <span className="text-xs text-zinc-400 line-through numeric" data-testid={`original-price-${product.id}`}>
                  {fmtPrice(product.store_price, currency)}
                </span>
              </div>
            ) : (
              <span className="font-heading text-lg font-extrabold text-[#002FA7]">
                {fmtPrice(product.store_price, currency)}
              </span>
            )}
            <span className="text-[10px] text-zinc-400">{currency}</span>
          </div>
        </div>
      </Link>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault(); e.stopPropagation();
          trackClick({ kind: trackKind, ref_id: trackRefId, section: "storefront_grid" });
          addItem(productForCart, 1);
        }}
        disabled={!canAdd}
        className={`mx-3 mb-3 mt-auto h-9 rounded-full text-white text-xs font-semibold inline-flex items-center justify-center gap-1.5 disabled:bg-zinc-300 disabled:cursor-not-allowed transition-colors ${promo ? "bg-[#E0000F] hover:bg-[#B0000C]" : "bg-zinc-900 hover:bg-zinc-800"}`}
        data-testid={`store-add-${product.id}`}
      >
        <Plus size={12} weight="bold" /> Agregar
      </button>
    </div>
  );
}



// Hasta 3 specs clave (highlight en el esquema; si no, las primeras) como chips.
function SpecChips({ product }) {
  const schemas = useSpecSchemas();
  const items = schemas.ordered(product.category, product.specs);
  if (items.length === 0) return null;
  const hl = items.filter((x) => x.highlight);
  const pick = (hl.length ? hl : items).slice(0, 3);
  return (
    <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`spec-chips-${product.id}`}>
      {pick.map((x) => (
        <span key={x.key} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 border border-zinc-200 leading-none" title={x.label}>{x.value}</span>
      ))}
    </div>
  );
}

function ComboCard({ combo }) {
  const { addItem } = useCart();
  const photo = (combo.photos || [])[0];
  const canAdd = (combo.max_qty || 0) > 0;
  const memberCount = (combo.members || []).length;
  const impressionRef = useImpressionTracker({ kind: "combo", ref_id: combo.id, section: "storefront_grid" });
  // Discount % vs original member total (if provided by the backend). Falls
  // back gracefully to null so we only show the badge when we can prove savings.
  const originalTotal = Number(combo.original_total || 0);
  const price = Number(combo.price || 0);
  const discountPct = originalTotal > price && originalTotal > 0
    ? Math.round((1 - price / originalTotal) * 100) : 0;
  const productLikeCombo = {
    id: combo.id,
    name: combo.name,
    brand: "COMBO",
    photos: photo ? [photo] : [],
    store_price: price,
    sale_currency: combo.currency || "MXN",
    max_qty: combo.max_qty,
    kind: "combo",
  };
  const comboHref = `/catalogo/combo/${combo.id}`;
  return (
    <div
      ref={impressionRef}
      className="group relative text-left bg-white border border-zinc-200 rounded-md overflow-hidden hover:shadow-md hover:border-[#7E57C2]/30 transition-all flex flex-col"
      data-testid={`combo-card-${combo.id}`}
    >
      {combo.free_shipping !== false && (
        <span
          className="absolute top-2 right-2 z-10 bg-emerald-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-sm tracking-wide shadow inline-flex items-center gap-1"
          data-testid={`combo-free-shipping-${combo.id}`}
          title="Envío gratis en Tijuana, Rosarito y Tecate"
        >
          🚚 Envío gratis
        </span>
      )}
      {/* Badges: COMBO on top, -X% right below (mirrors the promo card layout) */}
      <div className="absolute top-2 left-2 z-10 flex flex-col gap-1">
        <span className="bg-[#7E57C2] text-white text-[10px] font-extrabold px-2 py-0.5 rounded-sm tracking-wider shadow">
          {combo.badge_label || "COMBO"}
        </span>
        {discountPct > 0 && (
          <span className="bg-white border border-[#7E57C2] text-[#7E57C2] text-[10px] font-bold px-2 py-0.5 rounded-sm shadow-sm">
            -{discountPct}%
          </span>
        )}
      </div>
      <Link to={comboHref} className="block" data-testid={`combo-link-${combo.id}`}>
        <div className="aspect-square bg-zinc-50 overflow-hidden">
          {photo ? (
            <img
              src={photo}
              alt={combo.name}
              loading="lazy"
              className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-zinc-300 text-xs font-mono uppercase tracking-wider">
              📦 Combo
            </div>
          )}
        </div>
        <div className="p-3 pb-2">
          <div className="text-[10px] uppercase tracking-widest text-[#7E57C2] font-mono">
            {memberCount} producto{memberCount === 1 ? "" : "s"} incluidos
          </div>
          <div className="font-medium text-sm leading-tight line-clamp-2 min-h-[2.5rem]">{combo.name}</div>
          <div className="mt-2 flex items-baseline justify-between">
            <div className="flex items-baseline gap-2">
              <span className="font-heading text-lg font-extrabold text-[#7E57C2]">
                {fmtPrice(price, combo.currency)}
              </span>
              {originalTotal > price && (
                <span className="text-xs text-zinc-400 line-through numeric">
                  {fmtPrice(originalTotal, combo.currency)}
                </span>
              )}
            </div>
            <span className="text-[10px] text-zinc-400">{combo.currency || "MXN"}</span>
          </div>
          {combo.savings > 0 && (
            <div className="mt-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-1.5 py-0.5 inline-block">
              Ahorras {fmtPrice(combo.savings, combo.currency)}
            </div>
          )}
        </div>
      </Link>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault(); e.stopPropagation();
          trackClick({ kind: "combo", ref_id: combo.id, section: "storefront_grid" });
          addItem(productLikeCombo, 1);
        }}
        disabled={!canAdd}
        className="mx-3 mb-3 mt-auto h-9 rounded-full bg-[#7E57C2] hover:bg-[#5E37A2] text-white text-xs font-semibold inline-flex items-center justify-center gap-1.5 disabled:bg-zinc-300 disabled:cursor-not-allowed transition-colors"
        data-testid={`combo-add-${combo.id}`}
      >
        <Plus size={12} weight="bold" /> Agregar combo
      </button>
    </div>
  );
}



function CuratedSection({ section, vendorRef }) {
  const { addItem } = useCart();
  const accent = section.accent_color || "#002FA7";
  const sectionTitle = ({
    hero: "Destacados", ofertas_semana: "Ofertas de la semana",
    remates: "Remates", premium: "Premium",
    recomendados: "Recomendados", nuevos: "Nuevos ingresos",
  })[section.section] || section.name;
  const handleAdd = (it) => {
    // Normalize the resolved item into a cart-compatible "product"
    if (it.kind === "product") {
      const price = it.promotion ? it.promotion.promo_price : it.store_price;
      const currency = (it.promotion?.currency) || it.currency || "MXN";
      addItem({ id: it.id, name: it.name, brand: it.brand, photos: [it.photo].filter(Boolean), store_price: price, sale_currency: currency, max_qty: 5 }, 1);
    } else if (it.kind === "promotion") {
      addItem({ id: it.product_id, name: it.name, brand: it.brand, photos: [it.photo].filter(Boolean), store_price: it.promo_price, sale_currency: it.currency || "MXN", max_qty: 5 }, 1);
    } else if (it.kind === "combo") {
      addItem({ id: it.id, name: it.name, brand: "COMBO", photos: [it.photo].filter(Boolean), store_price: it.price, sale_currency: it.currency || "MXN", max_qty: it.max_qty || 5, kind: "combo" }, 1);
    }
  };
  return (
    <section data-testid={`section-${section.section}`}>
      {section.banner_url ? (
        <div className="relative aspect-[4/1] sm:aspect-[5/1] rounded-lg overflow-hidden mb-4">
          <img src={section.banner_url} alt={section.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent flex items-end p-4 sm:p-6">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/70 font-mono mb-1">{sectionTitle}</div>
              <h2 className="font-heading text-2xl sm:text-4xl font-extrabold tracking-tight text-white">{section.name}</h2>
              {section.description && <p className="text-white/80 text-xs sm:text-sm mt-1 max-w-xl">{section.description}</p>}
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-4 pb-3 border-b" style={{ borderColor: accent + "40" }}>
          <div className="text-[10px] uppercase tracking-widest font-mono mb-1" style={{ color: accent }}>{sectionTitle}</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight" style={{ color: accent }}>{section.name}</h2>
          {section.description && <p className="text-zinc-500 text-sm mt-1 max-w-2xl">{section.description}</p>}
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {section.items.map((it, idx) => {
          const href = it.kind === "combo" ? "#" : `/catalogo/p/${it.product_id || it.id}${vendorRef ? `?v=${encodeURIComponent(vendorRef)}` : ""}`;
          const isPromo = it.kind === "promotion" || it.promotion;
          const price = it.kind === "combo" ? it.price : (isPromo ? (it.promotion?.promo_price || it.promo_price) : it.store_price);
          const original = it.kind === "product" && it.promotion ? it.store_price : (it.kind === "promotion" ? it.store_price : null);
          const currency = (it.promotion?.currency) || it.currency || "MXN";
          const badge = it.kind === "combo" ? "COMBO" : (isPromo ? (it.promotion?.badge_label || it.badge_label || "PROMO") : null);
          const badgeColor = it.kind === "combo" ? "#7E57C2" : "#E0000F";
          return (
            <div key={`${it.kind}-${it.id}-${idx}`} className="group relative bg-white border border-zinc-200 rounded-md overflow-hidden hover:shadow-md transition-all flex flex-col" data-testid={`section-item-${it.id}`}>
              {badge && (
                <span className="absolute top-2 left-2 z-10 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-sm tracking-wider shadow" style={{ backgroundColor: badgeColor }}>{badge}</span>
              )}
              <Link to={href} className="block" onClick={(e) => it.kind === "combo" && e.preventDefault()}>
                <div className="aspect-square bg-zinc-50">
                  {it.photo ? <img src={it.photo} alt={it.name} loading="lazy" className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300" /> : <div className="w-full h-full flex items-center justify-center text-zinc-300 text-xs">Sin foto</div>}
                </div>
                <div className="p-3 pb-2">
                  {it.brand && <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{it.brand}</div>}
                  <div className="font-medium text-sm leading-tight line-clamp-2 min-h-[2.5rem]">{it.name}</div>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="font-heading text-lg font-extrabold" style={{ color: isPromo ? "#E0000F" : (it.kind === "combo" ? "#7E57C2" : "#002FA7") }}>
                      {fmtPrice(price, currency)}
                    </span>
                    {original && <span className="text-xs text-zinc-400 line-through">{fmtPrice(original, currency)}</span>}
                  </div>
                </div>
              </Link>
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAdd(it); }} className="mx-3 mb-3 mt-auto h-9 rounded-full text-white text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition-colors" style={{ backgroundColor: isPromo ? "#E0000F" : (it.kind === "combo" ? "#7E57C2" : "#18181b") }} data-testid={`section-add-${it.id}`}>
                <Plus size={12} weight="bold" /> Agregar
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}


/**
 * Pills mostrando la condición del equipo (Nuevo / Semi-nuevo /
 * Reacondicionado) y el grado estético si aplica (A/B/C). Visibles en
 * cualquier card o vista de listado de la tienda.
 */
export function ConditionBadges({ product, className = "" }) {
  const cond = product?.condition || "nuevo";
  const grade = product?.aesthetic_grade || null;
  const condMap = {
    nuevo:           { label: "Nuevo",           bg: "bg-emerald-600",  text: "text-white" },
    seminuevo:       { label: "Semi-nuevo",      bg: "bg-amber-500",    text: "text-white" },
    reacondicionado: { label: "Reacondicionado", bg: "bg-violet-600",   text: "text-white" },
  };
  const gradeMap = {
    A: { label: "Grado A", bg: "bg-emerald-100",  text: "text-emerald-800" },
    B: { label: "Grado B", bg: "bg-amber-100",    text: "text-amber-800" },
    C: { label: "Grado C", bg: "bg-red-100",      text: "text-red-800" },
  };
  const c = condMap[cond] || condMap.nuevo;
  const g = grade ? gradeMap[grade] : null;
  return (
    <div className={`inline-flex flex-wrap items-center gap-1 ${className}`} data-testid={`condition-badges-${product?.id || "x"}`}>
      <span className={`${c.bg} ${c.text} text-[10px] font-bold px-2 py-0.5 rounded-sm tracking-wide shadow-sm`}>{c.label}</span>
      {g && (
        <span className={`${g.bg} ${g.text} text-[10px] font-bold px-2 py-0.5 rounded-sm tracking-wide border border-current/10`}>{g.label}</span>
      )}
    </div>
  );
}
